import React, { useState, useCallback, useEffect } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { OrganizationEndpoints } from "@generated/organization";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { getCardStyles } from "src/shell/styles/themeUtils";

import type { Department, Team } from "../../types/OrganizationTypes";

import DepartmentDialog from "./teams/DepartmentDialog";
import TeamDialog from "./teams/TeamDialog";
import TeamsList from "./teams/TeamsList";

interface TeamsTabProps {
  orgAlias: string;
}

const DEFAULT_ACCESS_LEVEL = "Read only";

const TeamsTab: React.FC<TeamsTabProps> = ({ orgAlias }) => {
  const theme = useTheme();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Department dialog
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [deptName, setDeptName] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);

  // Team dialog
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamName, setTeamName] = useState("");
  const [teamAccessLevel, setTeamAccessLevel] = useState(DEFAULT_ACCESS_LEVEL);
  const [teamDeptId, setTeamDeptId] = useState<string>("");
  const [teamSaving, setTeamSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const token = orgSessionManager.getToken() ?? "";

  const fetchData = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [deptRes, teamRes] = await Promise.all([
        fetch(
          `${apiBaseUrls.BACKEND}${OrganizationEndpoints.LIST_DEPARTMENTS.replace(":alias", orgAlias)}`,
          { headers },
        ),
        fetch(
          `${apiBaseUrls.BACKEND}${OrganizationEndpoints.LIST_TEAMS.replace(":alias", orgAlias)}`,
          { headers },
        ),
      ]);
      const deptData = await deptRes.json();
      const teamData = await teamRes.json();
      setDepartments(deptData);
      setTeams(teamData);
    } catch {
      setError("Failed to load teams and departments");
    } finally {
      setLoading(false);
    }
  }, [orgAlias, token]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleCreateDepartment = async () => {
    setDeptSaving(true);
    setDialogError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.CREATE_DEPARTMENT.replace(":alias", orgAlias)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: deptName }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to create department");
      }
      await fetchData();
      setDeptDialogOpen(false);
      setDeptName("");
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : "Failed");
    } finally {
      setDeptSaving(false);
    }
  };

  const openCreateTeam = () => {
    setEditingTeam(null);
    setTeamName("");
    setTeamAccessLevel(DEFAULT_ACCESS_LEVEL);
    setTeamDeptId("");
    setDialogError(null);
    setTeamDialogOpen(true);
  };

  const openEditTeam = (team: Team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setTeamAccessLevel(team.accessLevel ?? DEFAULT_ACCESS_LEVEL);
    setTeamDeptId(team.departmentId ? String(team.departmentId) : "");
    setDialogError(null);
    setTeamDialogOpen(true);
  };

  const handleCreateTeam = async () => {
    setTeamSaving(true);
    setDialogError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.CREATE_TEAM.replace(":alias", orgAlias)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: teamName,
            accessLevel: teamAccessLevel,
            departmentId: teamDeptId ? Number(teamDeptId) : undefined,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to create team");
      }
      await fetchData();
      setTeamDialogOpen(false);
      setTeamName("");
      setTeamAccessLevel(DEFAULT_ACCESS_LEVEL);
      setTeamDeptId("");
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : "Failed");
    } finally {
      setTeamSaving(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!editingTeam) return;
    setTeamSaving(true);
    setDialogError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.UPDATE_TEAM.replace(":alias", orgAlias).replace(":id", String(editingTeam.id))}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: teamName,
            accessLevel: teamAccessLevel,
            departmentId: teamDeptId ? Number(teamDeptId) : null,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to update team");
      }
      await fetchData();
      setTeamDialogOpen(false);
      setEditingTeam(null);
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : "Failed");
    } finally {
      setTeamSaving(false);
    }
  };

  const handleDeleteTeam = async (id: number) => {
    await fetch(
      `${apiBaseUrls.BACKEND}${OrganizationEndpoints.DELETE_TEAM.replace(":alias", orgAlias).replace(":id", String(id))}`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
    );
    await fetchData();
  };

  if (loading) return <CircularProgress size={24} sx={{ mt: 2 }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ borderRadius: 1 }}>
        {error}
      </Alert>
    );

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          {teams.length} team{teams.length !== 1 ? "s" : ""}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDeptDialogOpen(true)}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Department
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreateTeam}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Team
          </Button>
        </Stack>
      </Stack>

      <Card sx={getCardStyles(theme)}>
        <TeamsList
          teams={teams}
          onDeleteTeam={(id) => void handleDeleteTeam(id)}
          onEditTeam={openEditTeam}
        />
      </Card>

      <DepartmentDialog
        open={deptDialogOpen}
        name={deptName}
        onNameChange={setDeptName}
        saving={deptSaving}
        error={dialogError}
        onClose={() => setDeptDialogOpen(false)}
        onCreate={() => void handleCreateDepartment()}
      />

      <TeamDialog
        open={teamDialogOpen}
        team={editingTeam}
        name={teamName}
        onNameChange={setTeamName}
        accessLevel={teamAccessLevel}
        onAccessLevelChange={setTeamAccessLevel}
        deptId={teamDeptId}
        onDeptIdChange={setTeamDeptId}
        departments={departments}
        saving={teamSaving}
        error={dialogError}
        onClose={() => {
          setTeamDialogOpen(false);
          setEditingTeam(null);
        }}
        onCreate={() =>
          void (editingTeam ? handleUpdateTeam() : handleCreateTeam())
        }
      />
    </Stack>
  );
};

export default TeamsTab;
